import { ResourceContext } from '@seddi/core';
import { IVector3 } from '@seddi/math-v2';

import { DomImagesContext } from './domImagesContext';
import { RecolorThread } from './recolorThread';

type Image = {
  id: string;
  sh: number;
  sw: number;
  palette: IVector3[];
  threadIdx: number;
};

export type ImageRecolor = {
  id: string;
  blobUrl: string;
};

export type RecolorContextOptions = {
  numThreads?: number;
};

const NUM_THREADS = 4;

export class RecolorContext extends ResourceContext<ImageRecolor> {
  /** Hidden canvas used for blob conversion. */
  private canvas: HTMLCanvasElement;
  /** Hidden canvas context used for blob conversion. */
  private canvasCtx: CanvasRenderingContext2D;
  /** DOM images context. */
  private domImagesCtx: DomImagesContext;
  /** Images context. */
  private imagesCtx: ResourceContext<Image> = new ResourceContext();
  /** Num threads. */
  private numThreads: number;
  /** Thread pool. */
  private threads: Array<RecolorThread> = [];
  /** Thread rotation. */
  private threadIdx: number = 0;

  /**
   * Creates a new context.
   */
  constructor(domImagesCtx: DomImagesContext, options?: RecolorContextOptions) {
    super();
    this.numThreads = options?.numThreads ?? NUM_THREADS;
    this.canvas = document.createElement('canvas');
    this.canvasCtx = this.canvas.getContext('2d');
    this.domImagesCtx = domImagesCtx;
  }

  /**
   * Returns the recolor with the given ID, or undefined if the recolor has not been loaded.
   * @param id ID of the recolor to retrieve.
   * @param color Target color.
   */
  public getEx(id: string, color: IVector3): ImageRecolor | undefined {
    id = this.getResourceId(id, color);
    return this.get(id);
  }

  /**
   * Init threads.
   */
  public async init(): Promise<void> {
    const promises = [];
    for (let i = 0; i < this.numThreads; ++i) {
      const thread = new RecolorThread();
      promises.push(thread.run());
      this.threads.push(thread);
    }
    await Promise.all(promises);
  }

  /**
   * Loads the image.
   * If the image is already recolored, returns the recolored image.
   * If the image is currently being loaded, returns an image recolor promise that will be resolved when the image finishes recoloring.
   * @param id Image id.
   * @param url Image url.
   * @param color RGB color
   */
  public async loadEx(
    id: string,
    url: string,
    color: IVector3,
  ): Promise<ImageRecolor> {
    const resourceId = this.getResourceId(id, color);

    await super.load(resourceId, async () => {
      // load image inside a thread
      const image = await this.loadImage(id, url);
      // recolor inside same thread
      const thread = this.threads[image.threadIdx];
      const data = await thread.recolorImage(id, color);
      // convert to blob
      const blobUrl = await this.rawToBlobUrl(data, image.sw, image.sh);
      const resource: ImageRecolor = { id, blobUrl };
      return resource;
    });

    return this.get(resourceId);
  }

  /**
   * Releases resources.
   */
  public async dispose(): Promise<void> {
    // release resources.
    this.resources.forEach(resource => {
      URL.revokeObjectURL(resource.item.blobUrl);
    });

    // terminate threads.
    for (const thread of this.threads) {
      thread.terminate();
    }
  }

  /**
   * Loads an image.
   * @param id Image id.
   * @param url Image url.
   */
  public async loadImage(id: string, url: string): Promise<Image> {
    await this.imagesCtx.load(id, async () => {
      // load dom image
      await this.domImagesCtx.loadEx(id, url);
      const dom = this.domImagesCtx.get(id);

      const threadIdx = this.threadIdx % this.numThreads;
      const thread = this.threads[threadIdx];
      this.threadIdx++;
      const palette = await thread.loadImage(dom);

      const image: Image = {
        id,
        sh: dom.sh,
        sw: dom.sw,
        palette,
        threadIdx,
      };
      return image;
    });

    return this.imagesCtx.get(id);
  }

  /**
   * Returns a unique task ID as a mixture of image id and color target.
   * @param id ID of the image.
   * @param color Color target.
   */
  private getResourceId(id: string, color: IVector3): string {
    const r = Number(color.x).toFixed(2);
    const g = Number(color.y).toFixed(2);
    const b = Number(color.z).toFixed(2);
    id = `${id}-${r}-${g}-${b}`;
    return id;
  }

  /**
   * Converts raw image data into an object url.
   * @param data The raw image data.
   * @param sw Image width.
   * @param sh Image height.
   * @returns The image object url.
   */
  private rawToBlobUrl(
    data: Uint8Array,
    sw: number,
    sh: number,
  ): Promise<string> {
    const { canvas, canvasCtx } = this;

    const imageData = new ImageData(new Uint8ClampedArray(data.buffer), sw, sh);
    canvas.width = imageData.width;
    canvas.height = imageData.height;
    canvasCtx.putImageData(
      imageData,
      0,
      0,
      0,
      0,
      imageData.width,
      imageData.height,
    );

    return new Promise<string>(resolve => {
      canvas.toBlob(
        blob => {
          const url = URL.createObjectURL(blob);
          resolve(url);
        },
        'image/png',
        1,
      );
    });
  }
}
